/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import {
  ACTIVITY,
  DASHBOARD,
  DEVICES,
  EDGES,
  NETWORKS,
  RADIOS,
  SETTINGS,
  SIMULATORS,
  VMZ,
  HARDWARE_INVENTORY,
  PROFILES,
  IT_DASHBOARD,
  DAILY_REPORT,
  ONBOARDING,
  CREATE_SITE,
  CREATE_VMZ,
  CREATE_RADIO_GROUP,
  CREATE_VMZ_GROUP,
  CREATE_APPLICATION,
  CREATE_DEVICE_GROUP,
  DEVICE_ADMINISTRATION,
  CREATE_NETWORK,
  DEPLOY_EDGE,
  CONNECT_NETWORKS_TO_EDGE,
  VMC_URL,
  USERS,
  EDGE_SSH,
} from "../constants/routes";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { CircularProgress } from '@h9/ui-lib';
import onAppStart from "./onAppStart";
import Layout from "./Layout";
import PermissionRoute from "./PermissionRoute";
import LoginExpiryAlert from "~/components/shared/LoginExpiryAlert";
import { useAppSelector } from "~/store/hooks";
import { settingsAirgapEnabled, settingsLoading, settingsSystemDashboardConsoleEnabled, settingsSystemOnboardingEnabled } from "~/store/context/settingsSlice";
import { AppSubject, CrudAction, useAppAbility, usePermissionsLoading } from "~/rbac";
import { useInterval } from "~/hooks/useInterval";
import evt from "~/store/event";
import { useCurrentRole } from "~/hooks/useCurrentRole";
import { ROLE_NAMES } from "~/services";
import { selectUserPermssions } from "~/store/context/authSlice";

const DefaultLandingRedirect = lazy(() => import("./DefaultLandingRedirect"));
const Dashboard = lazy(() => import("../views/dashboard/Dashboard"));
const RadiosPage = lazy(() => import("../views/radios/RadiosPage"));
const EdgesPage = lazy(() => import("../views/edges/EdgesPage"));
const DevicePage = lazy(() => import("../views/subscribers/DevicePage"));
const DnnPanel = lazy(() => import("../views/dnn/DnnPanel"));
const VMZone = lazy(() => import("../views/vmz"));
const Diagnostics = lazy(() => import("../views/diagnostics/SimulatorPanel"));
const Activity = lazy(() => import("../views/activity/index"));
const AdminPage = lazy(() => import("../views/adminstration/AdminPage"));
const UsersPage = lazy(() => import("../views/users/UsersPage"));
const NotImplemented = lazy(() => import("../views/NotImplemented/NotImplemented"));
const HardwareInventoryPanel = lazy(() => import("../views/hardwareInventory/HarwareInventoryPanel"));
const Profiles = lazy(() => import("../views/profiles/ProfilePanel"));
const CreateSite = lazy(() => import("../views/radios/Sites/Form"));
const ITDashboard = lazy(() => import("../views/ITDashboard"));
const Onboarding = lazy(() => import("../views/Onboarding/LandingPage"));
const CreateVmz = lazy(() => import("../views/vmz/zone/CreateVmz"));
const CreateRadioGroup = lazy(() => import("../views/radios/RadioGroup/Form"));
const CreateVmzGroup = lazy(() => import("../views/Onboarding/CreateVmzGroup"));
const CreateApplication = lazy(() => import("../views/Onboarding/CreateApplication"));
const CreateDeviceGroup = lazy(() => import("../views/subscribers/deviceGroups/CreateDeviceGroup"));
const DeviceAdministration = lazy(() => import("../views/subscribers/deviceGroups/DeviceAdministration"));
const CreateNetwork = lazy(() => import("../views/dnn/CreateNetwork"));
const DeployEdge = lazy(() => import("../views/Onboarding/DeployEdge"));
const ConnectNetworksToEdge = lazy(() => import("../views/Onboarding/ConnectNetworksToEdge"));
const EdgeSSHLogin = lazy(() => import("../views/edges/edgeSSH"));

type LState = {
  fromChat?: boolean;
} | undefined;

type Props = {};
const AppRoutes = (_props: Props) => {
  /**
   * Runs the onAppStart function when the component mounts.
   * This effect is responsible for performing any necessary setup or initialization tasks when the app starts.
   */
  useEffect(() => {
    onAppStart();
  }, []);

  useInterval(() => { evt.emit('refreshServiceController') }, 60000);

  const ability = useAppAbility();
  const permissionsMap = useAppSelector(selectUserPermssions);
  const permissionsLoading = usePermissionsLoading();
  const onboardingEnabled = useAppSelector(settingsSystemOnboardingEnabled);
  const monitorPageEnabled = useAppSelector(settingsSystemDashboardConsoleEnabled);
  const systemSettingsLoading = useAppSelector(settingsLoading);

  const history = useLocation<LState>();
  const [loading, setLoading] = useState(false);

  const { isKpiOnly: isKpiOnlyUser, isOperator, effectiveRoles } = useCurrentRole();

  // AI-RAN partners must not reach Profiles. The ai-ran branch enforced this by gating the
  // route on `zone.VIEW`; kept as an explicit role check so the intent survives regardless of
  // how CASL grants mobilityProfile/systemSettings to this role.
  const isAiRanPartner = effectiveRoles.includes(ROLE_NAMES.AI_RAN_PARTNER);

  const showOnboarding = useMemo(() => {
    if (isKpiOnlyUser) return false;
    return !systemSettingsLoading && !permissionsLoading && onboardingEnabled && (
      // At least on of the view permission should exist for the Getting Started page to be visible
      ability.can(CrudAction.view, AppSubject.radioSite) ||
      ability.can(CrudAction.view, AppSubject.radioGroup) ||
      ability.can(CrudAction.view, AppSubject.dnn) ||
      ability.can(CrudAction.view, AppSubject.deviceGroup) ||
      ability.can(CrudAction.view, AppSubject.zone) ||
      ability.can(CrudAction.view, AppSubject.edge) ||
      ability.can(CrudAction.view, AppSubject.device)
    )
  }, [isKpiOnlyUser, onboardingEnabled, systemSettingsLoading, permissionsLoading, ability]);

  const airgapEnabled = useAppSelector(settingsAirgapEnabled);

  useEffect(() => {
    if (history.state?.fromChat) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [history]);

  if (history.pathname === EDGE_SSH) {
    // Show spinner while permissions are being fetched on initial window load
    if (permissionsLoading || Object.keys(permissionsMap).length === 0) {
      return <Loading />;
    }
    // If the user is an operator and has permission to view edges, show the Edge SSH login page. Otherwise, show a "Not Implemented" page.
    if (isOperator && ability.can(CrudAction.view, AppSubject.edge)) {
      return (
        <Suspense fallback={<Loading />}>
          <EdgeSSHLogin />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<Loading />}>
        <NotImplemented />
      </Suspense>
    );
  }

  return (
    <Layout>
        <Suspense fallback={<Loading />}>
        { loading && <Loading overlay /> }
        <Switch>
          <Route exact path={VMC_URL} component={DefaultLandingRedirect} />
          <Route exact path={DASHBOARD} component={Dashboard} />
          <PermissionRoute exact path={RADIOS} subject={AppSubject.radio} component={RadiosPage} />
          <PermissionRoute exact path={EDGES} subject={[AppSubject.edge, AppSubject.dnn]} component={EdgesPage} />
          <PermissionRoute exact path={DEVICES} subject={AppSubject.device} component={DevicePage} />
          <PermissionRoute exact path={NETWORKS} subject={AppSubject.dnn} component={DnnPanel} />
          <PermissionRoute exact path={VMZ} subject={AppSubject.zone} condition={!isKpiOnlyUser} component={VMZone} />
          <PermissionRoute exact path={SIMULATORS} subject={AppSubject.simulator} condition={!isKpiOnlyUser} component={Diagnostics} />
          <PermissionRoute exact path={SETTINGS} subject={AppSubject.network} condition={!isKpiOnlyUser} component={AdminPage} />
          <PermissionRoute exact path={USERS} subject={AppSubject.user} condition={airgapEnabled} fallback={isOperator} component={UsersPage} />
          <PermissionRoute exact path={PROFILES} subject={[AppSubject.mobilityProfile, AppSubject.systemSettings]} condition={!isKpiOnlyUser && !isAiRanPartner} component={Profiles} />
          <PermissionRoute exact path={ACTIVITY} subject={AppSubject.log} component={Activity} />
          <PermissionRoute exact path={HARDWARE_INVENTORY} subject={AppSubject.vmcHardware} condition={!isKpiOnlyUser} component={HardwareInventoryPanel} />
          <PermissionRoute exact path={IT_DASHBOARD} subject={AppSubject.itAdminDashboard} condition={!isKpiOnlyUser && monitorPageEnabled} component={ITDashboard} />
          <PermissionRoute
            exact
            path={DAILY_REPORT}
            subject={AppSubject.itAdminDashboard}
            condition={!isKpiOnlyUser && monitorPageEnabled}
            render={() => <Redirect to={`${IT_DASHBOARD}?tab=dailyReport`} />}
          />
          { showOnboarding &&
            <Route path={ONBOARDING}>
              <Switch>
                {/* CreateSiteFlow */}
                <PermissionRoute exact path={CREATE_SITE} subject={AppSubject.radioSite} action={CrudAction.add} component={CreateSite} />
                {/* CreateVMZFlow */}
                <PermissionRoute exact path={CREATE_VMZ} subject={AppSubject.zone} action={CrudAction.add} component={CreateVmz} />
                {/* CreateVMZGroupFlow */}
                <PermissionRoute exact path={CREATE_VMZ_GROUP} subject={AppSubject.zoneGroup} action={CrudAction.add} component={CreateVmzGroup} />
                {/* CreateApplicationFlow */}
                <PermissionRoute exact path={CREATE_APPLICATION} subject={AppSubject.application} action={CrudAction.add} component={CreateApplication} />
                {/* CreateRadioGroupFlow */}
                <PermissionRoute exact path={CREATE_RADIO_GROUP} subject={AppSubject.radioGroup} action={CrudAction.add} component={CreateRadioGroup} />
                {/* CreateNetworkFlow */}
                <PermissionRoute exact path={CREATE_NETWORK} subject={AppSubject.dnn} action={CrudAction.add} component={CreateNetwork} />
                {/* CreateDeviceGroupFlow */}
                <PermissionRoute exact path={CREATE_DEVICE_GROUP} subject={AppSubject.deviceGroup} action={CrudAction.add} component={CreateDeviceGroup} />
                {/* Device Administration */}
                <PermissionRoute exact path={DEVICE_ADMINISTRATION} subject={AppSubject.device} action={CrudAction.edit} component={DeviceAdministration} />
                {/* DeployEdgeFlow */}
                <PermissionRoute exact path={DEPLOY_EDGE} subject={AppSubject.edge} action={CrudAction.add} component={DeployEdge} />
                {/* ConnectNetworksToEdgeFlow */}
                <PermissionRoute exact path={CONNECT_NETWORKS_TO_EDGE} subject={AppSubject.edge} action={CrudAction.edit} component={ConnectNetworksToEdge} />
                {/* Default onboarding view */}
                <Route path={ONBOARDING} component={Onboarding} />
              </Switch>
            </Route>
          }
          { !ability.can(CrudAction.view, AppSubject.zone) ? <Route component={() => <Redirect to={`${RADIOS}?view=aiRan`} />} /> : isKpiOnlyUser ? <Route component={() => <Redirect to={DASHBOARD} />} /> : <Route component={NotImplemented} />}
        </Switch>
      </Suspense>
      <LoginExpiryAlert />
    </Layout>
  );
};

export default AppRoutes;

type LoadingProps = {
  overlay?: boolean;
};

export function Loading({ overlay }: LoadingProps) {
  return (
    <div
      style={{
        position: overlay ? "absolute" : undefined,
        top: 0,
        left: 0,
        backdropFilter: "blur(3px)",
        zIndex: 1000,

        display: "flex",
        width: overlay ? "80vw" : "100%",
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CircularProgress color="primary" />
    </div>
  );
}
